/// <reference lib="dom" />
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { Invoice, Item, HhInvoice, HhInvoiceItem, InventoryItem, PurchaseOrder, PurchaseOrderItem } from 'shared/types.js';
import { formatDate, formatTime, formatDecimalIfNeeded, formatJobNumber, currencyFormatter } from './utils.js';
import { PoFilesDisplay } from './PoFilesDisplay.js';
import { api } from './api.js';

const DetailField = ({ label, value }: { label: string, value: React.ReactNode }) => (
    value ? <div><span className="meta-label">{label}:</span><span className="meta-value">{value}</span></div> : null
);

const toNum = (v: any) => {
    if (typeof v === 'string') v = v.replace(/\s+/g, '');
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

const safeText = (v: any) => (v === undefined || v === null || v !== v) ? '' : String(v);

export const validatePassword = (password: string) => {
    return {
        hasMinLength: password.length >= 8,
        hasNumber: /\d/.test(password),
        hasUpperCase: /[A-Z]/.test(password),
    };
};

export const PasswordRequirements = ({ password }: { password: string }) => {
    const { hasMinLength, hasNumber, hasUpperCase } = validatePassword(password);
    const CheckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;
    const CrossIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
    
    const requirementStyle = (isValid: boolean) => ({
        color: isValid ? 'var(--invoice-color)' : 'var(--text-muted-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        fontSize: '0.8rem',
        transition: 'color 0.2s'
    });

    return (
        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <div style={requirementStyle(hasMinLength)}>
                {hasMinLength ? <CheckIcon /> : <CrossIcon />}
                <span>At least 8 characters</span>
            </div>
            <div style={requirementStyle(hasNumber)}>
                {hasNumber ? <CheckIcon /> : <CrossIcon />}
                <span>At least one number</span>
            </div>
            <div style={requirementStyle(hasUpperCase)}>
                {hasUpperCase ? <CheckIcon /> : <CrossIcon />}
                <span>At least one uppercase letter</span>
            </div>
        </div>
    );
};

export const InventoryItemDetails = ({ item }: { item: InventoryItem }) => {
    const qoh = toNum(item.QOH);
    const qoo = toNum(item.QOO);
    const cqo = toNum(item.CQO);
    const promo = toNum(item.Promo_Price);
    const retail = toNum(item.Retail);
    const um = item.Stock_Unit;
    const qtyAvailable = qoh - cqo;
    const isPromo = promo > 0;
    const price = isPromo ? promo : retail;
    const [priceDollar, priceCent] = price.toFixed(2).split('.');
    const qtyColor = qtyAvailable < 0 ? 'var(--credit-color)' : 'inherit';
    return (
        <>
            <div className="inventory-description-card inventory-item-card">
                {safeText(item.Description)}
            </div>
            <div className="inventory-item-card">
                <div className="inventory-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    <div className="inventory-info-section">
                        <div className="label">UPC</div>
                        <div className="value">{safeText(item.UPC)}</div>
                    </div>
                    <div className="inventory-info-section">
                        <div className="label">SKU</div>
                        <div className="value">{safeText(item.SKU)}</div>
                    </div>
                    <div className="inventory-info-section">
                        <div className="label">MFG Part #</div>
                        <div className="value">{safeText(item.MFG_Part_Number)}</div>
                    </div>
                </div>
            </div>
            <div className="inventory-grid">
                <div className="inventory-item-card inventory-price-card">
                    {isPromo ? (
                        <>
                            <div className="price-label promo">PROMO PRICE</div>
                            <div className="primary-price">${priceDollar}<sup>{priceCent}</sup>/<sub>{um}</sub></div>
                            <div className="price-breakdown">
                                <div className="inventory-info-section">
                                    <div className="label">Retail Price</div>
                                    <div className="value" style={{fontSize: '1rem'}}>${retail.toFixed(2)}</div>
                                </div>
                                <div className="inventory-info-section">
                                    <div className="label">Promo Price</div>
                                    <div className="value" style={{fontSize: '1rem'}}>${promo.toFixed(2)}</div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="price-label">CURRENT PRICE</div>
                            <div className="primary-price">${priceDollar}<sup>{priceCent}</sup>/<sub>{um}</sub></div>
                        </>
                    )}
                    <div className="tax-note">Plus Tax</div>
                </div>
                <div className="inventory-item-card">
                    <div className="inventory-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                         <div className="inventory-info-section">
                            <div className="label">Qty Available</div>
                            <div className="value" style={{ color: qtyColor }}>{qtyAvailable}</div>
                        </div>
                        <div className="inventory-info-section">
                            <div className="label">Qty On Hand</div>
                            <div className="value">{qoh}</div>
                        </div>
                        <div className="inventory-info-section">
                            <div className="label">Committed</div>
                            <div className="value">{cqo}</div>
                        </div>
                        <div className="inventory-info-section">
                            <div className="label">Qty On Order</div>
                            <div className="value">{qoo}</div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const InventoryDetailModal = ({ sku, onClose }: { sku: string, onClose: () => void }) => {
    const [item, setItem] = useState<InventoryItem | 'loading' | 'not_found'>('loading');
    useEffect(() => {
        const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
        if (count === 0) {
            document.body.classList.add('modal-open');
        }
        document.body.dataset.modalOpenCount = String(count + 1);
        return () => {
            const count = parseInt(document.body.dataset.modalOpenCount || '0', 10);
            const newCount = Math.max(0, count - 1);
            if (newCount === 0) {
                document.body.classList.remove('modal-open');
            }
            document.body.dataset.modalOpenCount = String(newCount);
        };
    }, []);
    useEffect(() => {
        const fetchItem = async () => {
            if (!sku) return;
            setItem('loading');
            try {
                const response = await api.post<{ found: boolean, data?: InventoryItem, source?: string }>('/api/inventory/lookup', { searchTerm: sku });
                if (response.found && response.data) {
                    setItem(response.data);
                } else {
                    setItem('not_found');
                }
            } catch (error) {
                setItem('not_found');
            }
        };
        fetchItem();
    }, [sku]);
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal modal-inventory-detail" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <strong>Inventory Details: {sku}</strong>
                    <button onClick={onClose} aria-label="Close modal">&times;</button>
                </div>
                <div className="modal-body inventory-details">
                    {item === 'loading' && <div className="placeholder"><p>Loading...</p></div>}
                    {item === 'not_found' && <div className="inventory-not-found" style={{width: '100%'}}>Item <strong>{sku}</strong> not found.</div>}
                    {typeof item === 'object' && item !== null && (
                        <div className="inventory-lookup-container" style={{width: '100%', maxWidth: 'none', margin: 0, padding: 0}}>
                            <InventoryItemDetails item={item} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const HhInvoiceDetail = ({ invoice, items, onClear, onItemStatusChange, onClearAllItems, onClearInvoice, onClearWithIssues, onUpdateNotes, onSaveChanges, startInEditMode = false, canEditInvoices }: { invoice: HhInvoice, items: HhInvoiceItem[], onClear: () => void, onItemStatusChange: (itemId: number, isCleared: boolean) => void, onClearAllItems: () => void, onClearInvoice: () => void, onClearWithIssues: () => void, onUpdateNotes: (notes: string) => Promise<void>, onSaveChanges: (invoiceNo: string, items: HhInvoiceItem[], invoiceData: HhInvoice) => Promise<void>, startInEditMode?: boolean, canEditInvoices: boolean }) => {
    const [notes, setNotes] = useState(invoice.notes || '');
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editableItems, setEditableItems] = useState<HhInvoiceItem[]>([]);
    const [editableInvoice, setEditableInvoice] = useState<HhInvoice | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showJeldWenDiscount, setShowJeldWenDiscount] = useState(false);
    const nextNewItemId = useRef(-1);
    const isInitialRender = useRef(true);
    const handleEdit = useCallback(() => {
        setEditableItems(JSON.parse(JSON.stringify(items)));
        setEditableInvoice(JSON.parse(JSON.stringify(invoice)));
        setIsEditMode(true);
    }, [items, invoice]);
    useEffect(() => {
        setNotes(invoice.notes || '');
        setIsEditMode(false);
        setEditableItems([]);
        setEditableInvoice(null);
        setShowJeldWenDiscount(false);
        isInitialRender.current = true;
    }, [invoice]);
    useEffect(() => {
        if (isInitialRender.current && startInEditMode) {
            handleEdit();
        }
        isInitialRender.current = false;
    }, [startInEditMode, handleEdit]);
    const handleNotesSave = async () => {
        setIsSavingNotes(true);
        try {
            await onUpdateNotes(notes);
        } catch (error) {
            window.alert('Failed to save notes.');
        } finally {
            setIsSavingNotes(false);
        }
    };
    const handleCancel = () => {
        setIsEditMode(false);
        setEditableItems([]);
        setEditableInvoice(null);
    };
    const handleSave = async () => {
        if (!editableInvoice) return;
        setIsSaving(true);
        try {
            await onSaveChanges(invoice.invoice_no, editableItems, editableInvoice);
            setIsEditMode(false);
            setEditableInvoice(null);
        } catch (error) {
            window.alert('Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };
    const handleInvoiceChange = (field: keyof HhInvoice, value: string) => {
        if (editableInvoice) {
            setEditableInvoice(prev => prev ? { ...prev, [field]: value } : null);
        }
    };
    const handleItemChange = (index: number, field: keyof HhInvoiceItem, value: string) => {
        const newItems = editableItems.map((item, i) => {
            if (i !== index) return item;
            const updatedItem = { ...item, [field]: value };
            const qty = field === 'quantity' ? Number(value) || 0 : Number(updatedItem.quantity) || 0;
            const price = field === 'price' ? Number(value) || 0 : Number(updatedItem.price) || 0;
            const discount = field === 'discount' ? Number(value) || 0 : Number(updatedItem.discount) || 0;
            if (field === 'quantity') updatedItem.quantity = qty;
            if (field === 'price') updatedItem.price = price;
            if (field === 'discount') updatedItem.discount = discount;
            if (field === 'line_no') updatedItem.line_no = Number(value) || 0;
            updatedItem.extension = (qty * price) - discount;
            return updatedItem;
        });
        setEditableItems(newItems);
    };
    const handleAddItemRow = () => {
        const lastLineNo = editableItems.length > 0 ? Math.max(...editableItems.map(i => i.line_no)) : 0;
        const newItem: HhInvoiceItem = {
            id: nextNewItemId.current--,
            invoice_no: invoice.invoice_no,
            line_no: lastLineNo + 1,
            sku: '',
            description: '',
            quantity: 1,
            uom: 'EA',
            price: 0,
            discount: 0,
            extension: 0,
            is_cleared: 0,
        };
        setEditableItems([...editableItems, newItem]);
    };
    const handleDeleteItemRow = (index: number) => {
        if (window.confirm('Are you sure you want to delete this item?')) {
            const newItems = [...editableItems];
            newItems.splice(index, 1);
            setEditableItems(newItems);
        }
    };
    const totals = useMemo(() => {
        if (isEditMode) {
            const subtotal = editableItems.reduce((acc, item) => acc + (Number(item.extension) || 0), 0);
            const hst = subtotal * 0.15;
            const total = subtotal + hst;
            return { subtotal, hst, total };
        }
        if (showJeldWenDiscount && invoice.vendor === 'JELD-WEN MILLWORK DISTRIBUTION') {
            const subtotal = items.reduce((acc, item) => {
                const jeldWenPrice = (Number(item.price) || 0) * 0.9 * 1.03;
                const extension = (Number(item.quantity) || 0) * jeldWenPrice - (Number(item.discount) || 0);
                return acc + extension;
            }, 0);
            const hst = subtotal * 0.15;
            const total = subtotal + hst;
            return { subtotal, hst, total };
        }
        const subtotal = invoice.sub_total ?? items.reduce((acc, item) => acc + (Number(item.extension) || 0), 0);
        const hst = invoice.hst ?? subtotal * 0.15;
        const total = invoice.amount ?? subtotal + hst;
        return { subtotal, hst, total };
    }, [invoice, items, editableItems, isEditMode, showJeldWenDiscount]);
    const areAllItemsCleared = useMemo(() => {
        if (items.length === 0) return true;
        return items.every(item => item.is_cleared);
    }, [items]);
    const hasNoItems = items.length === 0;
    const currentItems = isEditMode ? editableItems : items;
    return (
        <div className="invoice-detail-container paper" key={invoice.invoice_no}>
            <button onClick={onClear} className="back-button">&larr; Back to list</button>
            <header className="invoice-header">
                <div className="invoice-meta-and-customer">
                    <div className="invoice-meta-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr' }}>
                        <DetailField label="Vendor" value={invoice.vendor} />
                        <DetailField label="Invoice Date" value={formatDate(invoice.invoice_date)} />
                        <DetailField label="Due Date" value={formatDate(invoice.due_date!)} />
                        {isEditMode && editableInvoice ? (
                            <div>
                                <span className="meta-label">PO Number</span>
                                <input
                                    type="text"
                                    value={editableInvoice.po_no || ''}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInvoiceChange('po_no', e.currentTarget.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.25rem',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        fontFamily: 'inherit',
                                        fontSize: '1rem',
                                        fontWeight: 'bold'
                                    }}
                                />
                            </div>
                        ) : (
                            <DetailField label="PO Number" value={invoice.po_no} />
                        )}
                    </div>
					<div className="invoice-meta-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr' }}>
                        <DetailField label="Vendor Cost" value={currencyFormatter.format(invoice.vendor_amount)} />
						<DetailField label="Discount" value={currencyFormatter.format(invoice.discount)} />
                        <DetailField label="Surcharge" value={currencyFormatter.format(invoice.surcharge)} />
                        <DetailField label="Subscribed Share" value={currencyFormatter.format(invoice.subscribed_share)} />
                        <DetailField label="5-Yr Note" value={currencyFormatter.format(invoice.five_yr_note)} />
                        <DetailField label="Advertising" value={currencyFormatter.format(invoice.advertising)} />
                        {(invoice.po_no || invoice.vendor_inv_no) && <div style={{gridColumn: 'span 4'}}><PoFilesDisplay poNumber={invoice.po_no || ''} reference={''} invoice_no={invoice.invoice_no} /></div>}
                    </div>
                    <div className="invoice-header-grid" style={{marginTop: '1rem'}}>
                        {invoice.vendor_inv_no ? (
                            <div className="customer-name-box">
                                <DetailField label="Vendor Invoice #" value={invoice.vendor_inv_no} />
                            </div>
                        ) : (
                            <div />
                        )}
                        <div className="invoice-title-box">
                            <h1>INVOICE: {invoice.invoice_no}</h1>
                            {invoice.vendor === 'JELD-WEN MILLWORK DISTRIBUTION' && !isEditMode && (
                                <button className="cost-view-toggle" onClick={() => setShowJeldWenDiscount(s => !s)}>
                                    {showJeldWenDiscount ? 'Show Regular Price' : 'Show JELD-WEN Price'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </header>
            <section className="table-container" style={{ margin: '2rem 0' }}>
                <table className="invoice-items-table">
                    <thead>
                        <tr>
                            <th style={{width: '5%'}}>Cleared</th>
                            <th style={{width: '5%'}}>Ln</th>
                            <th style={{width: '10%'}}>SKU</th>
                            <th>Description</th>
                            <th style={{width: '8%'}}>Qty</th>
							<th style={{width: '8%'}}>UOM</th>
                            <th style={{width: '10%'}} className="text-right">Price</th>
                            <th style={{width: '10%'}} className="text-right">Discount</th>
                            <th style={{width: '12%'}} className="text-right">Extension</th>
                            {isEditMode && <th style={{width: '5%'}}></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((item, index) => {
                            const jeldWenPrice = showJeldWenDiscount && invoice.vendor === 'JELD-WEN MILLWORK DISTRIBUTION'
                                ? (Number(item.price) || 0) * 0.9 * 1.03
                                : null;

                            const jeldWenExtension = jeldWenPrice !== null
                                ? (Number(item.quantity) || 0) * jeldWenPrice - (Number(item.discount) || 0)
                                : null;

                            return (
                                <tr key={item.id} style={{ textDecoration: !isEditMode && item.is_cleared ? 'line-through' : 'none', color: !isEditMode && item.is_cleared ? 'var(--text-muted-color)' : 'inherit' }}>
                                    <td style={{textAlign: 'center'}}>
                                        {canEditInvoices && <>
                                            <input
                                                type="checkbox"
                                                checked={!!item.is_cleared}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onItemStatusChange(item.id, e.currentTarget.checked)}
                                                aria-label={`Mark item ${item.line_no} as cleared`}
                                                style={{cursor: 'pointer'}}
                                                disabled={isEditMode}
                                            />
                                        </>}
                                    </td>
                                    {isEditMode ? (
                                        <>
                                            <td><input type="number" value={item.line_no} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemChange(index, 'line_no', e.currentTarget.value)} /></td>
                                            <td><input type="text" value={item.sku} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemChange(index, 'sku', e.currentTarget.value)} /></td>
                                            <td><textarea value={item.description} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleItemChange(index, 'description', e.currentTarget.value)} rows={2} /></td>
                                            <td className="text-right"><input type="number" value={item.quantity} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemChange(index, 'quantity', e.currentTarget.value)} /></td>
                                            <td className="text-right"><input type="text" value={item.uom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemChange(index, 'uom', e.currentTarget.value)} /></td>
                                            <td className="text-right"><input type="number" value={item.price} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemChange(index, 'price', e.currentTarget.value)} /></td>
                                            <td className="text-right"><input type="number" value={item.discount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemChange(index, 'discount', e.currentTarget.value)} /></td>
                                            <td className="text-right">{currencyFormatter.format(Number(item.extension) || 0)}</td>
                                            <td style={{textAlign: 'center'}}><button className="delete-item-btn" onClick={() => handleDeleteItemRow(index)}>&times;</button></td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{item.line_no}</td>
                                            <td>{item.sku}</td>
                                            <td>{item.description}</td>
                                            <td className="text-right">{formatDecimalIfNeeded(item.quantity)}</td>
                                            <td className="text-right">{formatDecimalIfNeeded(item.uom)}</td>
                                            <td className="text-right">{currencyFormatter.format((jeldWenPrice ?? Number(item.price)) || 0)}</td>
                                            <td className="text-right">{item.discount ? currencyFormatter.format(Number(item.discount)) : null}</td>
                                            <td className="text-right">{currencyFormatter.format((jeldWenExtension ?? Number(item.extension)) || 0)}</td>
                                        </>
                                    )}
                                </tr>
                            );
                        })}
                         {currentItems.length === 0 && (
                            <tr><td colSpan={isEditMode ? 10 : 9} style={{textAlign: 'center', padding: '2rem'}}>No line items found for this invoice.</td></tr>
                        )}
                    </tbody>
                    {isEditMode && (
                        <tfoot>
                            <tr>
                                <td colSpan={10} style={{textAlign: 'left', borderTop: '1px solid #000'}}>
                                    <button className="add-item-btn" onClick={handleAddItemRow} style={{marginTop: '1rem'}}>Add Item</button>
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </section>
            <footer className="invoice-footer-new">
                <div className="actions-container">
                    <div className="notes-section">
                        <label htmlFor="invoice-notes">Notes</label>
                        <textarea
                            id="invoice-notes"
                            value={notes}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.currentTarget.value)}
                            rows={4}
                            disabled={isEditMode}
                        />
                        <button
                            onClick={handleNotesSave}
                            disabled={isSavingNotes || notes === (invoice.notes || '') || isEditMode}
                            className="btn-primary"
                        >
                            {isSavingNotes ? 'Saving...' : 'Save Notes'}
                        </button>
                    </div>
                    <div className="action-buttons">
                        {isEditMode ? (
                            <>
                                <button onClick={handleSave} className="btn-primary" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Changes'}</button>
                                <button onClick={handleCancel} className="btn-primary danger" disabled={isSaving}>Cancel</button>
                            </>
                        ) : (
                           <>
                                {canEditInvoices && <>
                                    <button onClick={handleEdit} className="btn-primary">Edit Invoice</button>
                                    {hasNoItems ? (
                                        <button onClick={onClearInvoice} className="btn-primary" disabled={invoice.status === 'fully_cleared'}>Clear Invoice</button>
                                    ) : (
                                        <button onClick={onClearAllItems} className="btn-primary" disabled={areAllItemsCleared}>Clear All Items</button>
                                    )}
                                    <button onClick={onClearWithIssues} className="btn-primary danger" disabled={invoice.status === 'cleared_with_issues'}>Clear with Issues</button>
                                </>}
                           </>
                        )}
                    </div>
                </div>
                <div className="totals" style={{width: '400px', display: 'grid', gridTemplateColumns: '1fr', gap: '1rem'}}>
					<div>
                        <div className="total-row">
                            <span>Subtotal</span>
                            <span>{currencyFormatter.format(totals.subtotal)}</span>
                        </div>
                        <div className="total-row">
                            <span>HST (15%)</span>
                            <span>{currencyFormatter.format(totals.hst)}</span>
                        </div>
                        <div className="total-row grand-total">
                            <span>Total</span>
                            <span>{currencyFormatter.format(totals.total)}</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export const InvoiceDetail = ({ invoice, items, onClear, onSelectLinkedDoc, canViewCosts, canAccessInventoryLookup, onSaveChanges, canEditInvoices }: { invoice: Invoice, items: Item[], onClear: () => void, onSelectLinkedDoc?: (source: 'invoices' | 'save_orders', docNo: string) => void, canViewCosts: boolean, canAccessInventoryLookup: boolean, onSaveChanges?: (invoice: Invoice) => Promise<void>, canEditInvoices: boolean }) => {
    const [showCostView, setShowCostView] = useState(false);
    const [modalSku, setModalSku] = useState<string | null>(null);
    const poNumbersFromItems = useMemo(() => items.map(item => item.description), [items]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editableInvoice, setEditableInvoice] = useState<Invoice | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const getTitleText = () => {
        if (invoice.source === 'save_orders') {
            return 'SAVE ORDER';
        }
        if (invoice.type === 'Credit') {
            return 'CREDIT MEMO';
        }
        return 'INVOICE';
    };

    useEffect(() => {
        setIsEditMode(false);
        setEditableInvoice(null);
    }, [invoice]);
    const handleEdit = () => {
        setEditableInvoice(JSON.parse(JSON.stringify(invoice)));
        setIsEditMode(true);
    };
    const handleCancel = () => {
        setIsEditMode(false);
        setEditableInvoice(null);
    };
    const handleSave = async () => {
        if (!editableInvoice || !onSaveChanges) return;
        setIsSaving(true);
        try {
            await onSaveChanges(editableInvoice);
            setIsEditMode(false);
        } catch (error) {
            window.alert('Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };
    const handleInvoiceChange = (field: keyof Invoice, value: string) => {
        if (editableInvoice) {
            setEditableInvoice(prev => prev ? { ...prev, [field]: value } : null);
        }
    };
    const { subtotal, taxAmount, totalAsNumber } = useMemo(() => {
        if (showCostView && canViewCosts) {
            const costSubtotal = items.reduce((acc, item) => acc + (Number(item.sell_qty) || 0) * (Number(item.cost_stk) || 0), 0);
            const costTaxAmount = costSubtotal * 0.15;
            return { subtotal: costSubtotal, taxAmount: costTaxAmount, totalAsNumber: costSubtotal + costTaxAmount };
        } else {
            const priceTotal = Number(invoice.total);
            const priceSubtotal = priceTotal / 1.15;
            const priceTaxAmount = priceTotal - priceSubtotal;
            return { subtotal: priceSubtotal, taxAmount: priceTaxAmount, totalAsNumber: priceTotal };
        }
    }, [invoice, items, showCostView, canViewCosts]);
    return (
        <div className="invoice-detail-container paper" key={invoice.id}>
             <button onClick={onClear} className="back-button">&larr; Back to list</button>
            <header className="invoice-header">
                <div className="invoice-meta-and-customer">
                    <div className="invoice-meta-grid">
                        <div><span className="meta-label">CUST NO:</span><span className="meta-value">{invoice.cust_no}</span></div>
                        <div><span className="meta-label">JOB NO:</span><span className="meta-value">{formatJobNumber(invoice.job)}</span></div>
                        <div><span className="meta-label">REFERENCE:</span><span className="meta-value">{invoice.reference || ''}</span></div>
                        <div><span className="meta-label">CLERK:</span><span className="meta-value">{invoice.clerk}</span></div>
                        <div><span className="meta-label">DATE / TIME:</span><span className="meta-value">{formatDate(invoice.date)} {formatTime(invoice.time)}</span></div>
                        <div>
                            <span className="meta-label">PURCHASE ORDER:</span>
                            {isEditMode && editableInvoice ? (
                                <input
                                    type="text"
                                    value={editableInvoice.po_no || ''}
                                    onChange={(e) => handleInvoiceChange('po_no', e.currentTarget.value)}
                                    style={{
                                        width: '65%',
                                        padding: '0.25rem',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        fontFamily: 'inherit',
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                    }}
                                />
                            ) : (
                                <span className="meta-value">{invoice.po_no || ''}</span>
                            )}
                            <PoFilesDisplay poNumber={invoice.po_no} reference={invoice.reference} invoice_no={invoice.doc_no} additionalPoNumbers={poNumbersFromItems} />
                        </div>
					</div>
					<div className="invoice-header-grid">
						<div className="customer-name-box">
							<div>
								<span className="meta-label">CUSTOMER NAME:</span>
								<span className="meta-value">{invoice.customer_name}</span>
							</div>
							{invoice.linked_doc_no && onSelectLinkedDoc && (
								<div>
									<span className="meta-label">{invoice.linked_doc_source === 'save_orders' ? 'Save Order' : 'Invoice'}{' '}:</span>
									<span className="meta-value">{invoice.linked_doc_no}</span>
								</div>
							)}
						</div>
						<div className="invoice-title-box"><h1>{getTitleText()}: {invoice.doc_no}</h1>{canViewCosts && invoice.source === 'invoices' && <button className="cost-view-toggle" onClick={() => setShowCostView(!showCostView)}>{showCostView ? 'Show Prices' : 'Show Costs'}</button>}</div>
					</div>
                </div>
            </header>
            {invoice.source === 'invoices' ? (
                <section className="table-container">
                    <table className="invoice-items-table">
                        <thead>
                            <tr>
                                <th style={{width: '3%'}}>Ln</th>
                                <th style={{width: '6%'}}>Shipped</th>
                                <th style={{width: '6%'}}>Ordered</th>
                                <th style={{width: '4%'}}>UM</th>
                                <th style={{width: '12%'}}>SKU</th>
                                <th>Description</th>
                                <th style={{width: '6%'}}>Units</th>
                                <th style={{width: '8%'}} className="text-right">{showCostView && canViewCosts ? 'Cost' : 'Price'}</th>
                                <th style={{width: '10%'}} className="text-right">Extension</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => (
                                <tr key={item.id}>
                                    <td>{item.line_no}</td>
                                    <td>{formatDecimalIfNeeded(item.sell_qty)}</td>
                                    <td>{formatDecimalIfNeeded(item.sell_qty)}</td>
                                    <td>EA</td>
                                    <td>
                                        {item.sku && canAccessInventoryLookup ? (
                                            <button className="clickable-link" onClick={() => setModalSku(item.sku)}>{item.sku}</button>
                                        ) : (
                                            item.sku
                                        )}
                                    </td>
                                    <td>{item.description}</td>
                                    <td>{formatDecimalIfNeeded(item.sell_qty)}</td>
                                    <td className="text-right">{currencyFormatter.format(showCostView && canViewCosts ? (Number(item.cost_stk) || 0) : (Number(item.sell_price) || 0))}</td>
                                    <td className="text-right">{currencyFormatter.format(showCostView && canViewCosts ? (Number(item.cost_stk) * Number(item.sell_qty)) : (Number(item.extension) || 0))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            ) : (
                <section className="table-container" style={{padding: '2rem', textAlign: 'center', fontFamily: "'Inter', sans-serif", color: 'var(--text-muted-color)'}}>
                    <p>Save Orders do not have line items.</p>
                </section>
            )}
            <footer className="invoice-footer-new">
                 <div className="actions-container" style={{ flexGrow: 1, display: 'flex', alignItems: 'flex-start' }}>
                    {onSaveChanges && canEditInvoices && (
                        <div className="action-buttons">
                            {isEditMode ? (
                                <>
                                    <button onClick={handleSave} className="btn-primary" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Changes'}</button>
                                    <button onClick={handleCancel} className="btn-secondary" disabled={isSaving}>Cancel</button>
                                </>
                            ) : (
                                <button onClick={handleEdit} className="btn-primary">Edit PO Number</button>
                            )}
                        </div>
                    )}
                </div>
                <div className="totals">
                    <div className="total-row">
                        <span>Subtotal</span>
                        <span>{currencyFormatter.format(subtotal)}</span>
                    </div>
                    <div className="total-row">
                        <span>HST (15%)</span>
                        <span>{currencyFormatter.format(taxAmount)}</span>
                    </div>
                    <div className="total-row grand-total">
                        <span>Total</span>
                        <span>{currencyFormatter.format(totalAsNumber)}</span>
                    </div>
                </div>
            </footer>
            {modalSku && <InventoryDetailModal sku={modalSku} onClose={() => setModalSku(null)} />}
        </div>
    );
};

export const PurchaseOrderDetail = ({
    purchase,
    items,
    onClear,
}: {
    purchase: PurchaseOrder;
    items: PurchaseOrderItem[];
    onClear: () => void;
}) => {
    const isCreditPo = (purchase.type || '').trim().toLowerCase() === 'credit p.o.';
    const creditValueStyle = isCreditPo ? { color: 'var(--credit-color)' } : undefined;
    const formatPurchaseAmount = (value: number) => {
        const amount = Number(value) || 0;
        if (!isCreditPo) return currencyFormatter.format(amount);
        return `-${currencyFormatter.format(Math.abs(amount))}`;
    };
    const subtotal = useMemo(
        () => items.reduce((acc, item) => acc + (Number(item.extended_cost) || 0), 0),
        [items]
    );
    return (
        <div className="invoice-detail-container paper" key={`${purchase.po}-${purchase.date}`}>
            <button onClick={onClear} className="back-button">&larr; Back to list</button>
            <header className="invoice-header">
                <div className="invoice-meta-and-customer">
                    <div className="invoice-meta-grid">
                        <div><span className="meta-label">PO #:</span><span className="meta-value">{purchase.po}</span></div>
                        <div><span className="meta-label">TYPE:</span><span className="meta-value">{purchase.type}</span></div>
                        <div><span className="meta-label">VENDOR:</span><span className="meta-value">{purchase.vendor}</span></div>
                        <div><span className="meta-label">CLERK:</span><span className="meta-value">{purchase.clerk || ''}</span></div>
                        <div><span className="meta-label">DATE:</span><span className="meta-value">{formatDate(purchase.date)}</span></div>
                        <div><span className="meta-label">REF:</span><span className="meta-value">{purchase.ref || ''}</span></div>
                    </div>
                    <div className="invoice-header-grid">
                        <div className="customer-name-box" />
                        <div className="invoice-title-box"><h1>PURCHASE ORDER: {purchase.po}</h1></div>
                    </div>
                </div>
            </header>
            <section className="table-container">
                <table className="invoice-items-table">
                    <thead>
                        <tr>
                            <th style={{width: '6%'}}>Ln</th>
                            <th style={{width: '12%'}}>SKU</th>
                            <th>Description</th>
                            <th style={{width: '8%'}}>UM</th>
                            <th style={{width: '8%'}} className="text-right">Qty</th>
                            <th style={{width: '10%'}} className="text-right">Cost</th>
                            <th style={{width: '10%'}} className="text-right">Retail</th>
                            <th style={{width: '12%'}} className="text-right">Extended Cost</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr
                                key={`${item.po}-${item.line_no}-${index}`}
                                style={(item.status || '').trim().toUpperCase() === 'X' ? { textDecoration: 'line-through' } : undefined}
                            >
                                <td>{item.line_no}</td>
                                <td>{item.sku}</td>
                                <td>{item.description}</td>
                                <td>{item.um}</td>
                                <td className="text-right" style={creditValueStyle}>{isCreditPo ? `-${formatDecimalIfNeeded(Math.abs(Number(item.qty) || 0))}` : formatDecimalIfNeeded(item.qty)}</td>
                                <td className="text-right" style={creditValueStyle}>{formatPurchaseAmount(Number(item.cost) || 0)}</td>
                                <td className="text-right" style={creditValueStyle}>{formatPurchaseAmount(Number(item.retail) || 0)}</td>
                                <td className="text-right" style={creditValueStyle}>{formatPurchaseAmount(Number(item.extended_cost) || 0)}</td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={8} style={{textAlign: 'center', padding: '2rem'}}>No line items found for this purchase order.</td></tr>
                        )}
                    </tbody>
                </table>
            </section>
            <footer className="invoice-footer-new">
                <div className="actions-container" style={{ flexGrow: 1 }} />
                <div className="totals">
                    <div className="total-row">
                        <span>Subtotal</span>
                        <span style={creditValueStyle}>{formatPurchaseAmount(subtotal)}</span>
                    </div>
                    <div className="total-row grand-total">
                        <span>Total</span>
                        <span style={creditValueStyle}>{formatPurchaseAmount(Number(purchase.total_cost) || 0)}</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};
